import { useEffect, useMemo, useState } from "react"
import { useField } from "formik"
import { makeStyles } from "tss-react/mui"
import { Box, CircularProgress, Grid } from "@mui/material"

import {
    MeetingServicePriceListState,
    CreateOrderDialogFlow,
    CreateOrderDialogType,
    SelectedMainUserState,
    MeetingServicePriceListStatus,
} from "../../createOrderTypes"
import { useCreateOrderDialogType } from "../../hooks/useCreateOrderDialogType"
import { getMeetingServicePriceListStatusFlags } from "../../hooks/useMeetingServicePriceListStatus"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { DynamicConfigKey, InputFieldView, MeetingServiceConfig } from "../form/formTypes"
import {
    fetchProducts,
    FetchProductsResponse,
    MerchantProductsQuery,
} from "../../../../../modules/merchant/admin/products/productsApi"

import { NoProductsDialog } from "./NoProductsDialog"
import { ShopListHeader } from "./header/ShopListHeader"
import { filterShops } from "./helpers"
import { ProductContainer } from "./product/ProductContainer"

const useStyles = makeStyles()(() => ({
    container: {
        display: "grid",
        maxHeight: "100%",
        gridTemplateRows: "auto 1fr",
        overflow: "hidden",
        width: "100%",
    },
    loadingWrap: {
        height: "100%",
    },
}))
export type ShopFiltersTypes = Readonly<{
    isFavorite: boolean
    search: string
    isListView: boolean
}>

type ShopListProps = Readonly<{
    meetingServiceConfiguration?: MeetingServiceConfig
    handleCloseMainDialog: () => void
    flowConfig?: CreateOrderDialogFlow
    handleOpenSelectPriceListsDialog: () => void
    dialogType: CreateOrderDialogType
    user: SelectedMainUserState
    orderId?: number
}>

const transformProductsData = (data: FetchProductsResponse) => {
    return data.productGroups || []
}

export function ShopList({
    meetingServiceConfiguration,
    handleCloseMainDialog,
    flowConfig,
    handleOpenSelectPriceListsDialog,
    dialogType,
    user,
    orderId,
}: ShopListProps) {
    const { classes } = useStyles()
    const [{ value: priceListState }, , priceListHelpers] = useField<MeetingServicePriceListState>("priceList")
    const { item: priceList, status: priceListStatus } = priceListState
    const { isRefundOrder } = useCreateOrderDialogType(dialogType)

    const productCatalogView = meetingServiceConfiguration?.sections
        .flatMap(({ groups }) => groups.flatMap(({ fields }) => fields))
        .find((field) => field.key === DynamicConfigKey.PRODUCT_CATALOG)?.inputField?.view

    const [filters, setFilters] = useState<ShopFiltersTypes>(() => ({
        isFavorite: false,
        search: "",
        isListView: productCatalogView === InputFieldView.LIST,
    }))
    const searchRegex = useMemo(
        () => (filters.search ? new RegExp(`(${filters.search})`, "i") : undefined),
        [filters.search],
    )

    const {
        data: products = [],
        isLoading,
        isFetched,
        isError,
    } = useApiQuery(fetchProducts, {
        queryName: MerchantProductsQuery.LIST,
        dependencies: {
            merchantId: priceList?.merchantId!,
            priceListId: priceList?.priceListId!,
            isActive: true,
        },
        enabled: Boolean(priceList),
        select: transformProductsData,
    })

    const filteredShops = useMemo(
        () => filterShops(filters.isFavorite, searchRegex, products),
        [products, filters.isFavorite, searchRegex],
    )

    useEffect(() => {
        const {
            isError: isPriceListError,
            isNoProducts,
            isReady,
            isUnselected,
            isValidating,
        } = getMeetingServicePriceListStatusFlags(priceListStatus)

        if (!priceList?.priceListId) {
            if (!isUnselected) {
                priceListHelpers.setValue({ ...priceListState, status: MeetingServicePriceListStatus.UNSELECTED })
            }
            return
        }

        if (isLoading) {
            if (!isValidating) {
                priceListHelpers.setValue({ ...priceListState, status: MeetingServicePriceListStatus.VALIDATING })
            }
            return
        }

        if (isError) {
            if (!isPriceListError) {
                priceListHelpers.setValue({ ...priceListState, status: MeetingServicePriceListStatus.ERROR })
            }
            return
        }

        if (!isLoading && isFetched && products.length === 0) {
            if (!isNoProducts) {
                priceListHelpers.setValue({ ...priceListState, status: MeetingServicePriceListStatus.NO_PRODUCTS })
            }
            return
        }

        if (!isReady) {
            priceListHelpers.setValue({ ...priceListState, status: MeetingServicePriceListStatus.READY })
        }
    }, [isError, isFetched, isLoading, priceList?.priceListId, priceListStatus, products.length])

    if (isLoading) {
        return (
            <Grid container alignItems="center" justifyContent="center" className={classes.loadingWrap}>
                <CircularProgress size={70} />
            </Grid>
        )
    }

    const shouldShowNoProductsDialog =
        !isRefundOrder &&
        !isLoading &&
        isFetched &&
        getMeetingServicePriceListStatusFlags(priceListStatus).isNoProductsOrError

    if (shouldShowNoProductsDialog) {
        return (
            <NoProductsDialog
                handleCloseMainDialog={handleCloseMainDialog}
                handleOpenSelectPriceListsDialog={
                    !priceList?.isOnlyOnePriceList ? handleOpenSelectPriceListsDialog : undefined
                }
                isError={isError}
            />
        )
    }

    return (
        <Box className={classes.container}>
            <ShopListHeader
                filters={filters}
                setFilters={setFilters}
                meetingServiceConfiguration={meetingServiceConfiguration}
                handleCloseMainDialog={handleCloseMainDialog}
                flowConfig={flowConfig}
                dialogType={dialogType}
                user={user}
            />
            <ProductContainer
                list={filteredShops}
                searchRegex={searchRegex}
                isListView={filters.isListView}
                isFavoriteFilter={filters.isFavorite}
                handleCloseMainDialog={handleCloseMainDialog}
                isRefundOrder={isRefundOrder}
                orderId={orderId}
            />
        </Box>
    )
}
